import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, Check, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_PROJECTS = [
  {
    id: '1',
    title: { it: 'Sviluppo Settore Cabeci', en: 'Cabeci Sector Development', fr: 'Développement Secteur Cabeci', de: 'Entwicklung Sektor Cabeci', es: 'Desarrollo Sector Cabeci' },
    description: { it: 'Apertura di 15 nuove vie nel settore di Cabeci, con gradi dal 5c all\'8a.', en: 'Opening of 15 new routes in the Cabeci sector, with grades from 5c to 8a.', fr: 'Ouverture de 15 nouvelles voies dans le secteur de Cabeci.', de: 'Erschließung von 15 neuen Routen im Sektor Cabeci.', es: 'Apertura de 15 nuevas vías en el sector de Cabeci.' },
    status: 'completed',
    images: ['https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/exhuhqnq_Falesia%20di%20Cabeci.jpg'],
    start_date: '2024-01-01',
    end_date: '2024-06-30'
  },
  {
    id: '2',
    title: { it: 'Manutenzione Gazzara', en: 'Gazzara Maintenance', fr: 'Entretien Gazzara', de: 'Wartung Gazzara', es: 'Mantenimiento Gazzara' },
    description: { it: 'Sostituzione di chiodi e catene usurate nella falesia di Gazzara.', en: 'Replacement of worn bolts and chains in Gazzara crag.', fr: 'Remplacement des broches et chaînes usées à Gazzara.', de: 'Austausch von verschlissenen Haken und Ketten in Gazzara.', es: 'Reemplazo de chapas y cadenas desgastadas en Gazzara.' },
    status: 'in_progress',
    images: ['https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/2fwl0gnc_falesia%20gazzara.jpeg'],
    start_date: '2024-09-01',
    end_date: ''
  },
  {
    id: '3',
    title: { it: 'Nuovo Settore Via del Gatto', en: 'New Via del Gatto Sector', fr: 'Nouveau Secteur Via del Gatto', de: 'Neuer Sektor Via del Gatto', es: 'Nuevo Sector Via del Gatto' },
    description: { it: 'Esplorazione e sviluppo di un nuovo settore con potenziale per vie di alto livello.', en: 'Exploration and development of a new sector with potential for high-grade routes.', fr: 'Exploration et développement d\'un nouveau secteur.', de: 'Erkundung und Erschließung eines neuen Sektors.', es: 'Exploración y desarrollo de un nuevo sector.' },
    status: 'in_progress',
    images: [],
    start_date: '2024-10-01',
    end_date: ''
  }
];

export function ProjectsPage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await axios.get(`${API}/projects`);
        setProjects(res.data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div data-testid="projects-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FolderOpen className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('projects')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'I progetti su cui stiamo lavorando per sviluppare l\'arrampicata a Caltavuturo.'
                : 'The projects we are working on to develop climbing in Caltavuturo.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">{t('noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, i) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white border rounded-lg overflow-hidden group hover:border-[#FF6D00]/50 transition-colors"
                >
                  {/* Image */}
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={project.images?.[0] || 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=600'}
                      alt={getTranslatedContent(project.title)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      {project.status === 'completed' ? (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded uppercase">
                          <Check className="w-3 h-3" />
                          {t('completed')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded uppercase">
                          <Clock className="w-3 h-3" />
                          {t('inProgress')}
                        </span>
                      )}
                    </div>

                    <h3 className="font-heading text-xl uppercase mb-3">
                      {getTranslatedContent(project.title)}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {getTranslatedContent(project.description)}
                    </p>

                    {/* Gallery preview */}
                    {project.images?.length > 1 && (
                      <div className="flex gap-2 mt-4">
                        {project.images.slice(1, 4).map((img, j) => (
                          <div key={j} className="w-12 h-12 rounded overflow-hidden">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                        {project.images.length > 4 && (
                          <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                            +{project.images.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
